/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useRef, useState} from 'react'
import {
    Box,
    SimpleGrid,
    VStack,
    Text,
    Button,
    Center,
    useMultiStyleConfig,
    useStyleConfig,
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    ADD_FILTER_HIT_COUNT,
    REMOVE_FILTER_HIT_COUNT,
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'
import {CheckIcon} from '../../../components/icons'
import {remCalc} from '../../../theme/_utils'

const ColorRefinements = ({filter, toggleFilter, selectedFilters}) => {
    const {formatMessage} = useIntl()
    const swatchStyles = useMultiStyleConfig('SwatchGroup', {variant: 'circle', disabled: false})
    const customStyles = useStyleConfig('ColorRefinements')
    const [focusedIdx, setFocusedIdx] = useState(-1)
    const buttonsRef = useRef([])

    const items =
        filter?.values?.filter(
            (value) => value.hitCount !== 0 || selectedFilters.includes(value.value),
        ) || []

    const handleKeyDown = (e) => {
        if (!items.length) return

        // Left/Right arrows for navigation in grid layout
        if (e.key === 'ArrowRight') {
            e.preventDefault()
            setFocusedIdx((prev) => {
                const next = prev < items.length - 1 ? prev + 1 : 0
                buttonsRef.current[next]?.focus()
                return next
            })
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault()
            setFocusedIdx((prev) => {
                const next = prev > 0 ? prev - 1 : items.length - 1
                buttonsRef.current[next]?.focus()
                return next
            })
        }
        // Up/Down arrows for navigation between rows in grid
        else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault()
            // Calculate number of items per row (approximate based on typical layout)
            const itemsPerRow = 4 // Adjust based on your SimpleGrid columns setting

            let next = focusedIdx
            if (e.key === 'ArrowDown') {
                next = focusedIdx + itemsPerRow
                if (next >= items.length) {
                    // Wrap to beginning if we go past the end
                    next = next % items.length
                }
            } else {
                next = focusedIdx - itemsPerRow
                if (next < 0) {
                    // Wrap to end if we go past the beginning
                    next = items.length + next
                }
            }

            if (next >= 0 && next < items.length) {
                buttonsRef.current[next]?.focus()
                setFocusedIdx(next)
            }
        } else if ((e.key === ' ' || e.key === 'Enter') && focusedIdx >= 0) {
            e.preventDefault()
            const value = items[focusedIdx]
            const isSelected = selectedFilters.includes(value.value)
            toggleFilter(value, filter.attributeId, filter.label, isSelected)
        }
    }

    return (
        <Box onKeyDown={handleKeyDown} tabIndex={0} role="grid">
            <SimpleGrid {...customStyles?.grid} role="row">
                {items.map((value, idx) => {
                    const isSelected = selectedFilters.includes(value?.value)

                    const colorValues = value?.presentationId
                        ?.split(',')
                        .map((c) => `#${c.trim().toLowerCase()}`)

                    let backgroundStyle

                    if (colorValues?.length === 3) {
                        backgroundStyle = `linear-gradient(to bottom right, ${colorValues[0]} 20%, ${colorValues[1]} 50%, ${colorValues[2]} 80%)`
                    } else if (colorValues?.length === 2) {
                        backgroundStyle = `linear-gradient(to bottom right, ${colorValues[0]} 20%, ${colorValues[1]} 80%)`
                    } else {
                        backgroundStyle = colorValues?.[0]
                    }

                    return (
                        <Box {...customStyles?.gridItem} key={idx} role="gridcell">
                            <VStack
                                onClick={() =>
                                    toggleFilter(
                                        value,
                                        filter.attributeId,
                                        filter.label,
                                        isSelected,
                                    )
                                }
                                cursor="pointer"
                                id={`color-refinement-${idx}`}
                            >
                                <Box
                                    {...customStyles?.colorCircleContainer}
                                    style={{
                                        border: `${isSelected ? remCalc(1) : remCalc(0)} solid`,
                                    }}
                                >
                                    <Button
                                        {...swatchStyles.swatch}
                                        aria-checked={isSelected}
                                        variant="outline"
                                        aria-label={formatMessage(
                                            isSelected
                                                ? REMOVE_FILTER_HIT_COUNT
                                                : ADD_FILTER_HIT_COUNT,
                                            value,
                                        )}
                                        selected={isSelected}
                                        ref={(el) => (buttonsRef.current[idx] = el)}
                                        tabIndex={focusedIdx === idx ? 0 : -1}
                                    >
                                        <Center {...swatchStyles.swatchButton}>
                                            <Box
                                                {...customStyles?.colorCircle}
                                                background={backgroundStyle}
                                                position="relative"
                                            />
                                            {isSelected ? (
                                                <CheckIcon
                                                    position="absolute"
                                                    color={
                                                        colorValues.includes('#ffffff') ||
                                                        colorValues.includes('#fafafa')
                                                            ? 'black'
                                                            : 'white'
                                                    }
                                                />
                                            ) : null}
                                        </Center>
                                    </Button>
                                </Box>

                                <Box {...customStyles?.textContainer}>
                                    <Text {...customStyles?.stackText}>
                                        {formatMessage(
                                            {
                                                id: 'colorRefinements.label',
                                                defaultMessage: '{colorLabel}',
                                            },
                                            {colorLabel: value?.label},
                                        )}
                                    </Text>
                                    <Text {...customStyles?.hitCount}>
                                        {formatMessage(
                                            {
                                                id: 'colorRefinements.hitCount',
                                                defaultMessage: '({colorHitCount})',
                                            },
                                            {colorHitCount: value?.hitCount},
                                        )}
                                    </Text>
                                </Box>
                            </VStack>
                        </Box>
                    )
                })}
            </SimpleGrid>
        </Box>
    )
}

ColorRefinements.propTypes = {
    filter: PropTypes.object,
    toggleFilter: PropTypes.func,
    selectedFilters: PropTypes.array,
}

export default ColorRefinements
